import { z } from "zod";

/**
 * THE AGENT NARRATIVE CONTRACT.
 *
 * Read ADR-0006 before changing anything here.
 *
 * The pivot to paper trading split the regulatory posture along the
 * action/narrative line. Agents now DO place orders — that is gated
 * operationally by the PolicyGate (position limits, order caps, kill switch),
 * because an agent placing a simulated order is operating a simulator, not
 * advising anyone.
 *
 * This file guards the other half, which did not change. An agent that *says*
 * "you should sell NVDA" is making a recommendation whether or not it also
 * placed a simulated trade, and personalized recommendations to US retail
 * users generally require RIA registration.
 *
 * So this schema stays exactly as strict as it was. It is gate 2 of the
 * narrative gates; the others are the Postgres `kind` CHECK constraint, the
 * deterministic advice-phrase detector, the Haiku classifier, and the
 * red-team eval set. Adding a new insight kind requires touching all of them.
 *
 * Do not add a field that carries an action (`recommendation`,
 * `suggested_trade`, `target_weight`, ...). Order intent travels through the
 * OMS API, which is audited and policy-gated. It does not travel through
 * prose.
 */

export const INSIGHT_KINDS = [
  "exposure",
  "concentration",
  "sector_risk",
  "factor_risk",
  "drawdown",
  "correlation",
  "news_attribution",
  "data_quality",
] as const;

export type InsightKind = (typeof INSIGHT_KINDS)[number];

/** Severity is descriptive, not prescriptive: how notable, not how urgent to act. */
export const SEVERITY = {
  INFO: 0,
  NOTABLE: 1,
  ELEVATED: 2,
  HIGH: 3,
} as const;

/**
 * A single computed fact the narrative is allowed to rest on. Every insight
 * must cite at least one. The model does not compute these — `packages/analytics`
 * does, and they are passed in as evidence. This is what makes an insight
 * checkable rather than merely fluent.
 */
export const evidenceItemSchema = z.object({
  metric: z.string().min(1),
  value: z.number(),
  unit: z.enum(["percent", "usd", "ratio", "count", "days"]),
  as_of: z.iso.datetime(),
  source: z.enum(["computed", "broker", "market_data", "news"]),
});

export const entityRefSchema = z.object({
  type: z.enum(["symbol", "sector", "factor", "account"]),
  id: z.string().min(1),
});

export const insightSchema = z
  .object({
    kind: z.enum(INSIGHT_KINDS),
    severity: z.number().int().min(0).max(3),
    title: z.string().min(1).max(120),
    body_md: z.string().min(1).max(4000),
    evidence: z.array(evidenceItemSchema).min(1),
    entity_refs: z.array(entityRefSchema).default([]),
  })
  .strict(); // reject unknown keys — a model that invents `recommendation` fails validation

export type Insight = z.infer<typeof insightSchema>;
export type EvidenceItem = z.infer<typeof evidenceItemSchema>;

/** Bump when the shape changes; persisted on every row so evals can pin a version. */
export const INSIGHT_SCHEMA_VERSION = "1.0.0";

/** JSON Schema for the Claude `output_config.format` constraint. */
export const insightJsonSchema = z.toJSONSchema(insightSchema);
